import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ToggleLeft } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';

/** Settings → Features: server-side switches, all off until the owner flips them. */
export function FeaturesTab({ api, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get('/settings/features').then(r => setData(r.data)).catch(() => toast.error('Could not load features')); }, [api]);

  const flip = async (key, value) => {
    setBusy(key);
    try {
      const r = await api.put('/settings/features', { [key]: value });
      setData(r.data);
      toast.success(`${r.data.meta[key]?.name || key} ${value ? 'switched on' : 'switched off'}`);
      onChanged?.(r.data.flags);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>;
  return (
    <div className="space-y-4" data-testid="features-tab">
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}><ToggleLeft className="w-5 h-5 text-[#F5A623]" /> Features</h2>
        <p className="text-sm text-gray-400">Try new field-app features without touching the live system. Everything here is off by default; switching off hides the feature everywhere but keeps any data already recorded.</p>
      </div>
      {Object.entries(data.meta).map(([key, meta]) => (
        <Card key={key} className="bg-[#2A3240] border-white/10" data-testid={`feature-card-${key}`}>
          <CardContent className="p-5 flex items-start gap-4">
            <div className="flex-1">
              <div className="text-white font-semibold flex items-center gap-2">{meta.name}
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${data.flags[key] ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-white/5 text-gray-400 border-white/10'}`} data-testid={`feature-state-${key}`}>{data.flags[key] ? 'On' : 'Off'}</span>
              </div>
              <p className="text-sm text-gray-400 mt-1">{meta.blurb}</p>
            </div>
            <Switch checked={!!data.flags[key]} disabled={busy === key} onCheckedChange={(v) => flip(key, v)} className="data-[state=checked]:bg-[#F5A623] mt-1" data-testid={`feature-switch-${key}`} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
